import React, { useContext, useMemo, useState, useEffect } from 'react';
import { LanguageContext } from "../config/multilingualContext";
import MultiLingualContent from "../config/multilingualContent";
import FormattedNumber from './FormattedNumber';
import UnitRadioGroup from "./UnitRadioGroup";
import useConversionFactor from '../hooks/useConversionFactor';
import ExportDropdown from './ExportDropdown';
import { getLabel } from "../functions/lang";

const Detbalance = (props) => {
    const { configsColumn, tableStatData, configsLine, configsUnit, allUnits } = props;
    const { language } = useContext(LanguageContext);

    const colors = ['#f2ce61', '#fff2cc', '#94b8da', '#c6e0b4'];
    let colorIndex = -1;

    const [selected, setSelected] = useState(null);
    const [factor, setFactor] = useState(1);
    const [loadingFactor, setLoadingFactor] = useState(false);

    // Initialize selected unit
    useEffect(() => {
        if (selected == null) {
            if (configsUnit && (configsUnit.id || configsUnit.value)) {
                setSelected(String(configsUnit.id ?? configsUnit.value));
            } else {
                setSelected('7'); // fallback
            }
        }
    }, [allUnits, configsUnit, selected]);

    // Conversion factor
    const fromUnit = configsUnit?.id ?? (allUnits?.[0]?.id) ?? 7;
    const toUnit = selected;
    const conv = useConversionFactor(fromUnit, toUnit);

    useEffect(() => {
        if (conv) {
            setFactor(conv.factor ?? 1);
            setLoadingFactor(!!conv.loading);
        }
    }, [conv]);

    // Apply factor to table
    const displayTable = useMemo(() => {
        if (!tableStatData) return tableStatData;
        const out = {};
        Object.keys(tableStatData).forEach(lineId => {
            out[lineId] = {};
            Object.keys(tableStatData[lineId]).forEach(colId => {
                const raw = tableStatData[lineId][colId];
                const n = Number(raw);
                out[lineId][colId] = isFinite(n) ? n * Number(factor) : raw;
            });
        });
        return out;
    }, [tableStatData, factor]);

    // Build row labels
    const rowLabels = useMemo(() => {
        const map = {};
        if (configsLine?.length) {
            configsLine.forEach(item => {
                map[item.fk_data_id] = item.label_fr ?? item.label_en ?? item.label_pr ?? item.label_sw ?? item.fk_data_id;
            });
        }
        return map;
    }, [configsLine]);

    return (
        <div style={{ width: '100%' }}>
                {displayTable && Object.keys(displayTable).length > 0 && (
                    <>
                        <UnitRadioGroup
                            allUnits={allUnits}
                            selected={selected}
                            onChange={setSelected}
                        />

                        <ExportDropdown
                            tableData={displayTable}
                            columns={configsColumn}
                            filenamePrefix={`detbalance-${selected || 'unit'}`}
                            rowLabels={rowLabels}
                            configsLine={configsLine}
                            configsUnit={configsUnit}
                            language={language}
                            showUnitRow={false}
                            showTotal={true}
                            activeUnitLabel={allUnits && selected
                                ? (allUnits.find(u => String(u.id) === String(selected))?.['label_' + language.slice(0, 2)] || '')
                                : (configsUnit?.['label_' + language.slice(0, 2)] || '')
                            }
                            showUnitInHeaderLabels={false}
                        />
                    </>
                )}

                {loadingFactor && (
                    <div style={{ display: 'inline-block', marginLeft: 12 }}>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span className="ms-2">Loading...</span>
                    </div>
                )} 

            <div style={{ overflowY: 'scroll', height: '100vh' }}>
                {displayTable && Object.keys(displayTable).length > 0 ? (
                    <table className="table table-bordered stripe display mt-2" style={{ width: '100%', overflow: 'scroll' }}>
                        <thead>
                            <tr>
                                <th className="fixed-td"></th>
                                {configsColumn.length > 0
                                    ? configsColumn.map((col, idx) => (
                                        <th className="scrollable-td fixed-hd" key={idx}>
                                            {getLabel({
                                                label_fr: col.label_fr,
                                                label_en: col.label_en,
                                                label_pr: col.label_pr,
                                                label_sw: col.label_sw
                                            }, language)}
                                        </th>
                                    ))
                                    : <th className="fixed-td fixed-hd left-top-td"></th>}
                                <th>TOTAL</th>
                            </tr>
                        </thead>
                        <tbody>
                            {configsLine.map((row, idx) => {
                                if (row.level === 1 && idx !== 0) colorIndex++;
                                const levelClass = row.level === 1 ? 'fw-bold px-2' : row.level === 2 ? 'px-4' : 'px-5';

                                let total = 0;
                                return (
                                    <tr key={idx} style={{ backgroundColor: colors[colorIndex % colors.length] }}>
                                        <td className={`fixed-td ${levelClass}`}>
                                            {getLabel({
                                                label_fr: row.label_fr,
                                                label_en: row.label_en,
                                                label_pr: row.label_pr,
                                                label_sw: row.label_sw
                                            }, language)}
                                        </td>
                                        {configsColumn.map((col, cIdx) => {
                                            const rawVal = displayTable[row.fk_data_id]?.[col.id];
                                            const value = rawVal != null ? Number(rawVal) : null;
                                            if (value) total += value;
                                            return (
                                                <td className="scrollable-td" key={cIdx}>
                                                    {value != null ? <FormattedNumber value={value} locale={language} decimals={2} /> : '-'}
                                                </td>
                                            );
                                        })}
                                        <td>{total !== 0 ? <FormattedNumber value={total} locale={language} decimals={2} /> : '-'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    <p><MultiLingualContent contentID="nodata" /></p>
                )}
            </div>
        </div>
    );
};

export default Detbalance;